import { useState } from "react";
import { Trash2, ExternalLink, ArrowUpRight, ArrowDownRight, ChevronDown } from "lucide-react";

export const OUTCOMES = [
  { value: "win", label: "Win", color: "#2FD8A8" },
  { value: "loss", label: "Loss", color: "#EF4444" },
  { value: "be", label: "BE", color: "#94A3B8" },
  { value: "partial", label: "Parziale", color: "#F59E0B" },
];

export const DIRECTIONS = [
  { value: "buy", label: "Buy", color: "#64C8FF" },
  { value: "sell", label: "Sell", color: "#F59E0B" },
];

export const EMOTIONS = [
  "FOMO",
  "Revenge Trading",
  "Overtrading",
  "Entrata Anticipata",
  "No Stop Loss",
  "Stop Spostato",
  "Chiusura Anticipata",
  "Size Eccessiva",
  "Contro Trend",
  "Fuori dal Piano",
  "Noia",
  "Distrazione",
];

const outcomeMeta = (v) => OUTCOMES.find(o => o.value === v) || OUTCOMES[0];
const directionMeta = (v) => DIRECTIONS.find(d => d.value === v);

const rowBg = (outcome) => {
  if (outcome === "win") return "rgba(47, 216, 168, 0.08)";
  if (outcome === "loss") return "rgba(239, 68, 68, 0.08)";
  return "transparent";
};

const formatDT = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return dt; }
};

const cellInput = {
  background: "transparent",
  border: "1px solid transparent",
  color: "var(--mm-text-primary)",
  width: "100%",
  padding: "4px 6px",
  fontSize: "13px",
  outline: "none",
};

const EmotionBadges = ({ emotions }) => {
  if (!emotions || emotions.length === 0) return <span className="text-xs" style={{ color: "var(--mm-text-muted)" }}>—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {emotions.map((e) => (
        <span key={e} className="text-[10px] font-semibold px-1.5 py-0.5 whitespace-nowrap" style={{ color: "#F59E0B", backgroundColor: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}>{e}</span>
      ))}
    </div>
  );
};

const EmotionsCell = ({ trade, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const emotions = trade.emotions || [];
  const toggle = (e) => {
    const next = emotions.includes(e) ? emotions.filter((x) => x !== e) : [...emotions, e];
    onUpdate(trade.id, "emotions", next);
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold transition-colors hover:bg-white/5"
        style={{ border: "1px solid var(--mm-border)", color: emotions.length ? "#F59E0B" : "var(--mm-text-muted)" }}
        data-testid={`trade-emotions-${trade.id}`}>
        {emotions.length === 0 ? "Emotività" : emotions.length === 1 ? emotions[0] : `${emotions[0]} +${emotions.length - 1}`}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 p-3 grid grid-cols-2 gap-1.5" style={{ backgroundColor: "#0b1512", border: "1px solid var(--mm-border)", width: "320px", right: 0 }} data-testid={`emotions-dropdown-${trade.id}`}>
            {EMOTIONS.map((e) => {
              const active = emotions.includes(e);
              return (
                <button key={e} onClick={() => toggle(e)}
                  className="text-xs px-2 py-1.5 text-left font-semibold transition-colors"
                  style={{
                    border: `1px solid ${active ? "#F59E0B" : "var(--mm-border)"}`,
                    color: active ? "#F59E0B" : "var(--mm-text-secondary)",
                    backgroundColor: active ? "rgba(245,158,11,0.1)" : "transparent",
                  }}
                  data-testid={`emotion-opt-${trade.id}-${e.replace(/\s+/g, "-").toLowerCase()}`}>
                  {e}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const HEADERS = ["Data e Orario", "Pair", "Buy/Sell", "Screenshot", "Esito", "%", "Emotività", "Note", ""];

export const TradeTable = ({ trades, readOnly = false, onUpdate, onDelete }) => {
  if (!trades || trades.length === 0) {
    return (
      <div className="glass-card p-10 text-center" data-testid="trades-empty">
        <p style={{ color: "var(--mm-text-muted)" }}>Nessun trade registrato{readOnly ? "" : ". Aggiungi il tuo primo trade!"}</p>
      </div>
    );
  }

  const blurSave = (id, field, raw, original) => {
    const val = field === "percentage" ? (raw === "" ? 0 : parseFloat(raw)) : raw;
    if (val !== original && !(field === "percentage" && isNaN(val))) onUpdate(id, field, val);
  };

  return (
    <div className="glass-card overflow-x-auto" style={{ overflowY: "visible" }} data-testid="trades-table">
      <table className="w-full text-left" style={{ borderCollapse: "collapse", minWidth: "1050px" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--mm-border)" }}>
            {HEADERS.slice(0, readOnly ? 8 : 9).map((h, i) => (
              <th key={i} className="px-4 py-3 text-xs font-bold uppercase" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.1em" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => {
            const meta = outcomeMeta(t.outcome);
            const dir = directionMeta(t.direction);
            return (
              <tr key={t.id} data-testid={`trade-row-${t.id}`} style={{ backgroundColor: rowBg(t.outcome), borderBottom: "1px solid var(--mm-border)" }}>
                <td className="px-4 py-2" style={{ minWidth: "170px" }}>
                  {readOnly ? (
                    <span className="text-sm" style={{ color: "var(--mm-text-primary)" }}>{formatDT(t.trade_datetime)}</span>
                  ) : (
                    <input type="datetime-local" defaultValue={t.trade_datetime} className="trade-cell-input" style={cellInput}
                      onBlur={(e) => blurSave(t.id, "trade_datetime", e.target.value, t.trade_datetime)}
                      data-testid={`trade-dt-${t.id}`} />
                  )}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "95px" }}>
                  {readOnly ? (
                    <span className="text-sm font-semibold" style={{ color: "var(--mm-text-primary)" }}>{t.pair}</span>
                  ) : (
                    <input type="text" defaultValue={t.pair} className="trade-cell-input font-semibold" style={cellInput}
                      onBlur={(e) => blurSave(t.id, "pair", e.target.value.toUpperCase(), t.pair)}
                      data-testid={`trade-pair-${t.id}`} />
                  )}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "90px" }}>
                  {readOnly ? (
                    dir ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase" style={{ color: dir.color, backgroundColor: `${dir.color}1a`, border: `1px solid ${dir.color}55` }}>
                        {dir.value === "buy" ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{dir.label}
                      </span>
                    ) : <span className="text-xs" style={{ color: "var(--mm-text-muted)" }}>—</span>
                  ) : (
                    <select value={t.direction || "buy"} onChange={(e) => onUpdate(t.id, "direction", e.target.value)}
                      className="trade-cell-input" style={{ ...cellInput, color: (dir || DIRECTIONS[0]).color, fontWeight: 700, cursor: "pointer" }}
                      data-testid={`trade-direction-${t.id}`}>
                      {DIRECTIONS.map(d => <option key={d.value} value={d.value} style={{ backgroundColor: "#0b1512", color: d.color }}>{d.label}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "125px" }}>
                  <div className="flex items-center gap-2">
                    {!readOnly && (
                      <input type="text" defaultValue={t.screenshot_url || ""} placeholder="Link..." title={t.screenshot_url || "Incolla il link dello screenshot"} className="trade-cell-input" style={{ ...cellInput, maxWidth: "100px" }}
                        onBlur={(e) => blurSave(t.id, "screenshot_url", e.target.value, t.screenshot_url)}
                        data-testid={`trade-screenshot-${t.id}`} />
                    )}
                    {t.screenshot_url ? (
                      <a href={t.screenshot_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-semibold flex-shrink-0" style={{ color: "var(--mm-gold)" }} data-testid={`trade-screenshot-link-${t.id}`}>
                        <ExternalLink size={13} /> {readOnly ? "Apri" : ""}
                      </a>
                    ) : readOnly ? <span className="text-xs" style={{ color: "var(--mm-text-muted)" }}>—</span> : null}
                  </div>
                </td>
                <td className="px-4 py-2" style={{ minWidth: "105px" }}>
                  {readOnly ? (
                    <span className="px-2 py-1 text-xs font-bold uppercase" style={{ color: meta.color, backgroundColor: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>{meta.label}</span>
                  ) : (
                    <select value={t.outcome} onChange={(e) => onUpdate(t.id, "outcome", e.target.value)}
                      className="trade-cell-input" style={{ ...cellInput, color: meta.color, fontWeight: 700, cursor: "pointer" }}
                      data-testid={`trade-outcome-${t.id}`}>
                      {OUTCOMES.map(o => <option key={o.value} value={o.value} style={{ backgroundColor: "#0b1512", color: o.color }}>{o.label}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "75px" }}>
                  {readOnly ? (
                    <span className="text-sm font-semibold" style={{ color: (t.percentage || 0) >= 0 ? "#2FD8A8" : "#EF4444" }}>{t.percentage != null ? `${t.percentage}%` : "—"}</span>
                  ) : (
                    <input type="number" step="0.01" defaultValue={t.percentage ?? ""} className="trade-cell-input" style={{ ...cellInput, maxWidth: "75px" }}
                      onBlur={(e) => blurSave(t.id, "percentage", e.target.value, t.percentage)}
                      data-testid={`trade-pct-${t.id}`} />
                  )}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "140px" }}>
                  {readOnly ? <EmotionBadges emotions={t.emotions} /> : <EmotionsCell trade={t} onUpdate={onUpdate} />}
                </td>
                <td className="px-4 py-2" style={{ minWidth: "160px" }}>
                  {readOnly ? (
                    <span className="text-sm" style={{ color: "var(--mm-text-secondary)", whiteSpace: "pre-wrap" }}>{t.notes || "—"}</span>
                  ) : (
                    <input type="text" defaultValue={t.notes || ""} placeholder="Note..." className="trade-cell-input" style={cellInput}
                      onBlur={(e) => blurSave(t.id, "notes", e.target.value, t.notes)}
                      data-testid={`trade-notes-${t.id}`} />
                  )}
                </td>
                {!readOnly && (
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDelete(t.id)} className="p-1.5 transition-colors hover:bg-red-500/20" style={{ border: "1px solid rgba(239,68,68,0.35)" }} data-testid={`trade-delete-${t.id}`} aria-label="Elimina trade">
                      <Trash2 size={14} style={{ color: "#EF4444" }} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
